import { t } from 'i18next';
import { useState } from 'react';

import EmailDocumentsDialog from '~components/EmailDocumentsDialog/EmailDocumentsDialog';
import { OutboundEmailEntry } from '~hooks/useOutboundEmails/sendOutboundEmails';
import {
  DEFAULT_SETTLEMENT_TICKET_ATTACHMENT_OPTIONS,
  SettlementTicketAttachmentOptions,
} from '~hooks/useSettlements/models';
import useSettlementEmailDraftsQuery from '~queries/useSettlementEmailDraftsQuery';

import TicketAttachmentOptions from './TicketAttachmentOptions';

interface EmailSettlementsDialogProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSend: (
    emails: OutboundEmailEntry[],
    ticketOptions: SettlementTicketAttachmentOptions,
  ) => Promise<void>;
  settlementIds: string[];
}

function EmailSettlementsContent({
  companyId,
  onClose,
  onSend,
  settlementIds,
}: Omit<EmailSettlementsDialogProps, 'isOpen'>) {
  const [ticketOptions, setTicketOptions] = useState(
    DEFAULT_SETTLEMENT_TICKET_ATTACHMENT_OPTIONS,
  );
  const { drafts, isError, isLoading, refetch } = useSettlementEmailDraftsQuery(
    companyId,
    settlementIds,
    ticketOptions,
  );
  const emailCount = drafts?.length || settlementIds.length;
  const isSingleEmail = settlementIds.length === 1;

  const renderTicketOptions = ({ isSubmitting }: { isSubmitting: boolean }) => (
    <TicketAttachmentOptions
      isDisabled={isSubmitting}
      onChange={setTicketOptions}
      value={ticketOptions}
    />
  );

  return (
    <EmailDocumentsDialog
      drafts={drafts}
      filesFooterContent={isSingleEmail ? renderTicketOptions : undefined}
      headerContent={isSingleEmail ? undefined : renderTicketOptions}
      isError={isError}
      isLoading={isLoading}
      labels={{
        description: t('approvals.driver_pay.settlements.email_settlement.description', {
          count: emailCount,
        }),
        noEmails: t('approvals.driver_pay.settlements.email_settlement.no_emails'),
        send: t('approvals.driver_pay.settlements.email_settlement.send', {
          count: emailCount,
        }),
        title: t('approvals.driver_pay.settlements.email_settlement.title', {
          count: emailCount,
        }),
      }}
      onClose={onClose}
      onRetry={refetch}
      onSend={(emails) => onSend(emails, ticketOptions)}
    />
  );
}

export default function EmailSettlementsDialog({
  isOpen,
  ...contentProps
}: EmailSettlementsDialogProps) {
  return isOpen ? <EmailSettlementsContent {...contentProps} /> : null;
}
